// External
import React, { useState, useMemo, useCallback, memo } from "react";
import _ from "lodash";
import { useLazyQuery, useMutation } from "@apollo/client";

// DS
import { Modal, Button, Tabs } from "@toddle-edu/ds-web";
import { DownloadOutlined, InformationOutlined } from "@toddle-edu/ds-icons";

// Hooks
import { useDispatch, useI18n } from "Hooks";

// Module
import { downloadFromUrl } from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { useAcademicRecordsModal } from "../../../../context";
import { fetchTranscriptV3BulkUploadSkippedRowsExcelQuery } from "../../modules/BulkUploadQueries";
import { commitTranscriptV3BulkUploadFromExcelMutation } from "../../modules/BulkUploadMutations";
import {
  SHEET_TYPES,
  OPERATION_TYPES,
  MODAL_WIDTH,
  ACCEPTED_FILE_FORMATS,
} from "../../modules/BulkUploadConstants";
import { getReviewDataTabs } from "../../modules/BulkUploadHelpers";

// Components
import BulkUploadReviewDataGrid from "./BulkUploadReviewDataGrid";
import FullScreenLoader from "UIComponents/FullScreenLoader";

// Styles
import classes from "./BulkUploadReviewModal.scss";

const TEST_ID_PREFIX = "bulk-upload-review-modal";

const BulkUploadReviewModal = () => {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const {
    modalInfo: { metadata = {} } = {},
    closeModal,
  } = useAcademicRecordsModal();

  const { sheetType, transactionId, summary } = metadata;
  const skippedCount = _.get(summary, "skippedCount", 0);

  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  const tabItems = useMemo(() => getReviewDataTabs({ summary, t }), [
    summary,
    t,
  ]);

  const [activeTab, setActiveTab] = useState(
    () => tabItems?.[0]?.value ?? null
  );

  const isSkipTab = activeTab === OPERATION_TYPES.SKIP;
  const isUpdateTab = activeTab === OPERATION_TYPES.UPDATE;

  // Only skip tab with no other tabs = all rows failed
  const isConfirmDisabled = isSkipTab && _.size(tabItems) === 1;

  const refetchQueriesForSheetType = useMemo(
    () =>
      sheetType === SHEET_TYPES.RATINGS
        ? ["getStudentTranscriptAcademicRecords", "getTranscriptResources"]
        : ["getStudentPersonalDetails"],
    [sheetType]
  );

  const [commitUpload] = useMutation(
    commitTranscriptV3BulkUploadFromExcelMutation
  );

  const [downloadSkippedRecords] = useLazyQuery(
    fetchTranscriptV3BulkUploadSkippedRowsExcelQuery,
    {
      fetchPolicy: "no-cache",
      onCompleted: data => {
        setShowFullScreenLoader(false);
        const url = _.get(
          data,
          "transcript.fetchTranscriptV3BulkUploadSkippedRowsExcel.url"
        );
        if (url) {
          dispatch(
            downloadFromUrl({
              attachment: {
                url,
                name: t("common:skipped_records"),
                mimeType: ACCEPTED_FILE_FORMATS,
              },
              shouldAskForSave: true,
            })
          );
        }
      },
      onError: () => {
        setShowFullScreenLoader(false);
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      },
    }
  );

  const handleDownloadSkippedRecords = useCallback(() => {
    setShowFullScreenLoader(true);
    downloadSkippedRecords({
      variables: {
        input: { transactionId },
      },
    });
  }, [downloadSkippedRecords, transactionId]);

  const handleConfirmUpload = useCallback(async () => {
    setShowFullScreenLoader(true);
    try {
      const { data } = await commitUpload({
        variables: {
          input: { transactionId },
        },
        refetchQueries: refetchQueriesForSheetType,
        awaitRefetchQueries: true,
      });

      setShowFullScreenLoader(false);
      const isSuccess = _.get(
        data,
        "transcript.commitTranscriptV3BulkUploadFromExcel.isSuccess"
      );

      if (isSuccess) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:entity_updated_successfully",
            locale_params: [
              {
                key: "entity",
                value: "common:data",
                isPlainText: false,
              },
            ],
            type: "success",
          })
        );
        closeModal();
      } else {
        const warning = _.get(
          data,
          "transcript.commitTranscriptV3BulkUploadFromExcel.warning"
        );
        dispatch(setToastMsg(warning || "toastMsgs:something_went_wrong"));
      }
    } catch {
      setShowFullScreenLoader(false);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  }, [
    commitUpload,
    transactionId,
    refetchQueriesForSheetType,
    closeModal,
    dispatch,
  ]);

  const handleTabChange = useCallback(value => {
    setActiveTab(value);
  }, []);

  return (
    <>
      <Modal
        isOpen={true}
        height="764px"
        width={MODAL_WIDTH.REVIEW}
        onClose={closeModal}
      >
        <Modal.Header
          title={t("transcript:review_changes_from_uploaded_file")}
          isClosable={true}
          hasSeparator={true}
        />
        <Modal.Body isScrollable>
          <div className={classes.reviewBody}>
            <div className={classes.tabsContainer}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                options={tabItems}
                variant="inline"
                hasSeparator
              />
            </div>
            {(isUpdateTab || isSkipTab) && (
              <div className={classes.descriptionContainer}>
                <InformationOutlined size="xxx-small" variant="subtle" />
                <span className="text-secondary text-body-s">
                  {isUpdateTab
                    ? t("transcript:fields_highlighted_yellow_updated")
                    : t("transcript:records_with_errors_skipped")}
                </span>
              </div>
            )}
            <div className={classes.dataGridContainer}>
              {activeTab && (
                <BulkUploadReviewDataGrid
                  transactionId={transactionId}
                  operationType={activeTab}
                />
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer hasSeparator={true}>
          <div className={classes.modalFooter}>
            {skippedCount > 0 && isSkipTab && (
              <Button
                icon={<DownloadOutlined />}
                variant="neutral"
                type="plain"
                size="medium"
                onClick={handleDownloadSkippedRecords}
                testId={`${TEST_ID_PREFIX}-download-skipped`}
              >
                {t("transcript:download_skipped_records")}
              </Button>
            )}
            <div className={classes.actionButtons}>
              <Button
                variant="neutral"
                type="outlined"
                size="medium"
                onClick={closeModal}
                testId={`${TEST_ID_PREFIX}-cancel`}
              >
                {t("common:cancel")}
              </Button>
              <Button
                variant="progressive"
                type="fill"
                size="medium"
                disabled={isConfirmDisabled}
                onClick={handleConfirmUpload}
                testId={`${TEST_ID_PREFIX}-confirm`}
              >
                {t("transcript:confirm_upload")}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
      {showFullScreenLoader && <FullScreenLoader />}
    </>
  );
};

export default memo(BulkUploadReviewModal);
